(function() {
  'use strict';

  var initializedAttribute = 'data-resilient-video-initialized';
  var requestedAttribute = 'data-resilient-video-requested';
  var retryDelays = [100, 400, 1200, 2500];
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = navigator.connection && navigator.connection.saveData;

  function hasCurrentData(video) {
    return video.readyState >= 2;
  }

  function isRendered(video) {
    return !video.getClientRects || video.getClientRects().length > 0;
  }

  function configureAutoplay(video) {
    // WebKit can parse the muted/playsinline attributes before its media
    // element state is fully initialized. Set both the properties and the
    // attributes immediately before play() so a cold navigation is treated as
    // eligible muted inline playback.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
  }

  function showFallback(video) {
    video.classList.remove('resilient-video__video--ready');
  }

  function showVideoAfterPaint(video) {
    if (video.classList.contains('resilient-video__video--ready')) return;

    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(function() {
        if (!video.paused && hasCurrentData(video)) {
          video.classList.add('resilient-video__video--ready');
        }
      });
      return;
    }

    // Older Safari has no requestVideoFrameCallback. Two animation frames after
    // `playing` gives WebKit a paint opportunity before the fallback is hidden.
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        if (!video.paused && hasCurrentData(video)) {
          video.classList.add('resilient-video__video--ready');
        }
      });
    });
  }

  function clearRetry(video) {
    if (video.__kbResilientRetryTimer) {
      window.clearTimeout(video.__kbResilientRetryTimer);
      video.__kbResilientRetryTimer = null;
    }
  }

  function shouldPlay(video) {
    return video.hasAttribute(requestedAttribute) &&
      !reduceMotion &&
      !saveData &&
      document.visibilityState !== 'hidden' &&
      isRendered(video);
  }

  function scheduleRetry(video) {
    if (!shouldPlay(video) || video.__kbResilientRetryTimer) return;

    var retryIndex = video.__kbResilientRetryCount || 0;
    if (retryIndex >= retryDelays.length) return;

    video.__kbResilientRetryCount = retryIndex + 1;
    video.__kbResilientRetryTimer = window.setTimeout(function() {
      video.__kbResilientRetryTimer = null;
      attemptPlayback(video);
    }, retryDelays[retryIndex]);
  }

  function attemptPlayback(video) {
    if (!shouldPlay(video) || video.__kbResilientPlayPending) return;

    configureAutoplay(video);

    var attempt;
    try {
      attempt = video.play();
    } catch (error) {
      showFallback(video);
      scheduleRetry(video);
      return;
    }

    if (!attempt || typeof attempt.then !== 'function') {
      if (!video.paused && hasCurrentData(video)) showVideoAfterPaint(video);
      return;
    }

    video.__kbResilientPlayPending = true;
    attempt.then(function() {
      video.__kbResilientPlayPending = false;
      clearRetry(video);
      video.__kbResilientRetryCount = 0;
      if (!video.paused && hasCurrentData(video)) showVideoAfterPaint(video);
    }).catch(function() {
      video.__kbResilientPlayPending = false;
      showFallback(video);
      scheduleRetry(video);
    });
  }

  function retryWhenReady(video) {
    if (!shouldPlay(video) || !video.paused) return;
    clearRetry(video);
    attemptPlayback(video);
  }

  function initVideo(video) {
    if (!video || video.hasAttribute(initializedAttribute)) return;
    video.setAttribute(initializedAttribute, 'true');

    video.addEventListener('playing', function() {
      showVideoAfterPaint(video);
    });
    video.addEventListener('error', function() {
      showFallback(video);
    });
    video.addEventListener('emptied', function() {
      showFallback(video);
    });
    video.addEventListener('loadedmetadata', function() {
      retryWhenReady(video);
    });
    video.addEventListener('loadeddata', function() {
      retryWhenReady(video);
    });
    video.addEventListener('canplay', function() {
      retryWhenReady(video);
    });

    if (reduceMotion || saveData) {
      video.pause();
      showFallback(video);
      return;
    }

    if (!video.paused && hasCurrentData(video)) {
      showVideoAfterPaint(video);
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('video[data-resilient-video]')) initVideo(scope);
    scope.querySelectorAll('video[data-resilient-video]').forEach(initVideo);
  }

  function play(video) {
    if (!video) return;
    initVideo(video);

    // A still image is the intended experience for these user preferences.
    if (reduceMotion || saveData) {
      video.pause();
      showFallback(video);
      return;
    }

    video.setAttribute(requestedAttribute, 'true');
    video.__kbResilientRetryCount = 0;
    clearRetry(video);
    configureAutoplay(video);

    var addedSource = false;
    video.querySelectorAll('source[data-src]').forEach(function(source) {
      source.setAttribute('src', source.getAttribute('data-src'));
      source.removeAttribute('data-src');
      addedSource = true;
    });
    if (addedSource) video.load();

    attemptPlayback(video);
  }

  function resumeRequestedVideos() {
    document.querySelectorAll('video[' + requestedAttribute + ']').forEach(function(video) {
      if (!shouldPlay(video)) return;
      video.__kbResilientRetryCount = 0;
      clearRetry(video);
      attemptPlayback(video);
    });
  }

  window.KBResilientVideo = {
    init: init,
    play: play,
    showFallback: showFallback
  };

  init(document);
  document.addEventListener('DOMContentLoaded', function() { init(document); });
  document.addEventListener('shopify:section:load', function(event) { init(event.target); });
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') resumeRequestedVideos();
  });
  window.addEventListener('pageshow', resumeRequestedVideos);
  window.addEventListener('online', resumeRequestedVideos);

  // If the browser blocks the cold-start request despite muted autoplay, the
  // next real interaction supplies a user activation and gives playback one
  // more chance without requiring a page refresh.
  document.addEventListener('touchstart', resumeRequestedVideos, true);
  document.addEventListener('pointerdown', resumeRequestedVideos, true);
})();
